import { useQueryClient } from '@tanstack/react-query';
import React, { FC, ReactElement, useCallback, useEffect, useRef } from 'react';
import { FieldErrors, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { InvestmentRoundingSetting } from 'shared';
import { v4 as uuidv4 } from 'uuid';

import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { FadeIn } from '../../../../components/motion/index';
import { Sheet, SHEET_CLOSE_ANIMATION_MS, SheetContent } from '../../../../components/sheet/index';
import { SignatureRestoreAccessConfirmModal } from '../../../../components/signature-restore-access-confirm-modal';
import { QueryKey } from '../../../../constants/query-key.const';
import { PendingMutationSettleOutcome } from '../../../../hooks/event/pending-events.store';
import {
  EventHooks,
  useLocale,
  useModalState,
  useReactForm,
  useRevokedAccessBeforeSend,
} from '../../../../hooks/index';
import {
  backendToFormTypeMapping,
  CompanyIncorporationEvent,
  eventFormType,
  EventFormType,
  EventTypeBackend,
  EventTypeButtonTitle,
  eventTypeMapping,
  EventTypeTitleString,
  EventUnion,
  GrantEvent,
} from '../../../../types/events.types';
import { sharePlanTypeTitle } from '../../../../types/pool-plans.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { getTranslation } from '../../../../utils/get-translation.util';
import { handleFormInvalidField } from '../../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import {
  EditEventModalBodyPlaceholder,
  EditEventModalFooterPlaceholder,
  EditEventModalHeaderPlaceholder,
} from '../../components/events-loading-placeholders';
import { ConfirmUpdateEventModal } from '../../modals/index';
import { isExerciseBackedEventType } from '../../utils/exercise-event-edit-enabled.util';
import {
  grantEditPreviewStepFromPersistedGrant,
  grantUsesTemplateSignatureWizard,
} from '../../utils/grant-edit-signature-flow.util';
import { describeInvalidFormField } from '../describe-invalid-form-field';
import { ChildrenFormProps, EditEventStepVariant } from '../event-form.types';
import {
  FormSchema,
  formSchema,
  GrantSignatureOption,
  GrantSignatureOptionType,
} from '../event-form.validation';
import { GrantStepFirst } from '../step-components/grant-steps/grant-step-first';
import { GrantStepThird } from '../step-components/grant-steps/grant-step-third';
import {
  CompanyIncorporationEditPreview,
  CompanyIncorporationStepOne,
  CompanyIncorporationStepThree,
  CompanyIncorporationStepTwo,
} from '../step-components/index';
import { EditPreview, OptionalStep, StepOne, StepTwo } from '../steps/index';
import {
  mapCompanyIncorporationEventToForm,
  mapCompanyIncorporationFormToUpdateDto,
  resolveCompanyIncorporationManualFileLinks,
} from '../utils/company-incorporation-event-form.util';
import {
  isDocusignFileLink,
  mergeEventGeneralFileLinksForSave,
  mergeExerciseFamilyFileLinksForSave,
  mergeTransactionFileLinksForSave,
  resolveEventAllDocusignFileLinks,
  resolveEventGeneralManualFileLinks,
  resolveExerciseFamilyManualFileLinks,
  resolveTransactionManualFileLinks,
} from '../utils/resolve-linked-signature-file-links.util';
import {
  mapReverseVestingFormToDto,
  mapReverseVestingToForm,
} from '../utils/reverse-vesting-form.util';
import { resolveInvalidBaseEditStep } from './resolve-invalid-edit-step';

type ModalProps = {
  companyId: string;
  defaultData?: {
    isOpen?: boolean;
  };
  eventId: string;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  issuedShares: number;
  onEditClick: (props: { id: string; step: EditEventStepVariant }) => void;
  previewMode?: true;
  step: EditEventStepVariant;
  transactionId?: string;
};

const baseEditFormSteps: ((
  props: {
    onEditClick: (step: EditEventStepVariant) => void;
  } & ChildrenFormProps,
) => ReactElement)[] = [
  (props) => <StepOne {...props} />,
  (props) => <OptionalStep {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <EditPreview {...props} />,
];

const companyIncorporationEditFormSteps: EditFormStepRenderer[] = [
  (props) => <CompanyIncorporationStepOne {...props} />,
  (props) => <CompanyIncorporationStepTwo {...props} />,
  (props) => <CompanyIncorporationStepThree {...props} />,
  (props) => <CompanyIncorporationEditPreview {...props} />,
];

type EditFormStepRenderer = (
  props: {
    onEditClick: (step: EditEventStepVariant) => void;
  } & ChildrenFormProps,
) => ReactElement;

const grantEditUsesSignatureRecipientsStep = (
  event: EventUnion | null | undefined,
  grantSlice:
    | { selectedTemplateId?: null | string; signatureOption?: GrantSignatureOptionType }
    | undefined,
): boolean => {
  if (!event || event.type !== EventTypeBackend.GRANT) return false;
  if (grantSlice?.signatureOption === GrantSignatureOption.WITHOUT_SIGNATURE) {
    return false;
  }
  if (grantSlice?.signatureOption === GrantSignatureOption.SIGNATURE) {
    return true;
  }
  const ge = event as GrantEvent;
  return grantUsesTemplateSignatureWizard(ge);
};

const grantEditFormStepsWithRecipients: EditFormStepRenderer[] = [
  (props) => <GrantStepFirst {...props} />,
  (props) => <StepOne {...props} />,
  (props) => <GrantStepThird {...props} />,
  (props) => <OptionalStep {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <EditPreview {...props} />,
];

const grantEditFormStepsWithoutRecipients: EditFormStepRenderer[] = [
  (props) => <GrantStepFirst {...props} />,
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <EditPreview {...props} />,
];

const buildSanitizedGrantSignatoryAssignments = (
  signatoryAssignments:
    | Record<string, { fullName?: string; id: string } | null | undefined>
    | undefined,
): Record<string, { id: string }> => {
  return Object.fromEntries(
    Object.entries(signatoryAssignments ?? {})
      .filter(([, assignment]) => assignment?.id)
      .map(([signatoryTypeId, assignment]) => [signatoryTypeId, { id: assignment!.id }]),
  );
};

export const EditEventModal: FC<ModalProps> = ({
  companyId,
  issuedShares,
  isOpenModal,
  handleClose,
  invalidateQuery,
  onEditClick,
  eventId,
  step,
  previewMode,
  transactionId,
  defaultData,
}) => {
  const queryClient = useQueryClient();
  const { event } = EventHooks.useEvent({ companyId, eventId });
  const { update, isPending: isEventUpdatePending } = EventHooks.useUpdate();
  const { isPending: isExerciseDocumentsPatchPending, patchExerciseEventDocuments } =
    EventHooks.usePatchExerciseEventDocuments();
  const isPending = isEventUpdatePending || isExerciseDocumentsPatchPending;
  const {
    checkAndProceed: runRevokedAccessCheck,
    clearState: clearRevokedAccessModalState,
    handleConfirmRestore,
    isChecking: isCheckingRevokedAccess,
    revokedSigners: revokedAccessSigners,
  } = useRevokedAccessBeforeSend();
  const { messagesLocale } = useLocale();

  const {
    control,
    reset,
    setValue,
    getValues,
    getFieldState,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
    mode: 'onChange',
  });
  const { initialStep } = getValues();

  const grantSliceForEditSteps = useWatch({
    control,
    name: 'stepOne.eventDetails.grant',
  });

  const hydratedInitialStep = useWatch({
    control,
    name: 'initialStep',
  });

  const grantEditPreviewSyncedForEventIdRef = useRef<null | string>(null);
  const grantEditFormHydratedForEventIdRef = useRef<null | string>(null);
  const wasEditModalOpenRef = useRef(false);
  const originalInvestmentRoundingSettingRef = useRef<InvestmentRoundingSetting | undefined>(
    undefined,
  );

  const invalidateVaultQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_HARDCODED_REPOSITORY] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_HARDCODED_REPOSITORY_FILES] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_OPEN_REPOSITORY] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_OPEN_FILES] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDER] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDERS] });
  }, [queryClient]);

  useEffect(() => {
    grantEditFormHydratedForEventIdRef.current = null;
    grantEditPreviewSyncedForEventIdRef.current = null;
  }, [eventId]);

  useEffect(() => {
    if (!isOpenModal) {
      grantEditPreviewSyncedForEventIdRef.current = null;
      grantEditFormHydratedForEventIdRef.current = null;
      return;
    }
    if (!event || event.type !== EventTypeBackend.GRANT) return;
    if (grantEditPreviewSyncedForEventIdRef.current === eventId) return;

    grantEditPreviewSyncedForEventIdRef.current = eventId;
    const previewStep = grantEditPreviewStepFromPersistedGrant(event as GrantEvent);
    onEditClick({ id: eventId, step: previewStep });
  }, [event, eventId, isOpenModal, onEditClick]);

  useEffect(() => {
    if (!isOpenModal || !event || event.type !== EventTypeBackend.GRANT) return;
    const usesRecipients = grantEditUsesSignatureRecipientsStep(event, grantSliceForEditSteps);
    const maxStep = usesRecipients ? 6 : 4;
    if (step > maxStep) {
      onEditClick({ id: eventId, step: maxStep as EditEventStepVariant });
    }
  }, [event, eventId, grantSliceForEditSteps, isOpenModal, onEditClick, step]);

  useEffect(() => {
    if (!event) return;
    if (grantEditFormHydratedForEventIdRef.current === eventId) return;
    grantEditFormHydratedForEventIdRef.current = eventId;

    const grantEvent = event.type === EventTypeBackend.GRANT ? (event as GrantEvent) : null;

    let grantSignatureFormFields: Record<string, unknown> = {};
    if (grantEvent) {
      if (grantEvent.selectedTemplateId) {
        grantSignatureFormFields = {
          signatureOption: GrantSignatureOption.SIGNATURE,
          selectedTemplateId: grantEvent.selectedTemplateId,
          signatoryAssignments: grantEvent.signatoryAssignments ?? {},
        };
      } else {
        grantSignatureFormFields = {
          signatureOption: GrantSignatureOption.WITHOUT_SIGNATURE,
        };
      }
    }

    const mapFileLinksToFormFiles = (links: string[]) =>
      links.map((link) => ({
        abort: () => '',
        loadProgress: 100 as const,
        doc: {
          name: getS3FileOriginalName(link) || '',
          size: 10,
          type: 'application/pdf',
        },
        docLink: link,
        id: uuidv4(),
      }));

    const resolveManualFileLinksForEdit = (sourceEvent: EventUnion) =>
      isExerciseBackedEventType(sourceEvent.type)
        ? resolveExerciseFamilyManualFileLinks(sourceEvent)
        : resolveEventGeneralManualFileLinks(sourceEvent);

    const files = mapFileLinksToFormFiles(resolveManualFileLinksForEdit(event));

    reset({
      initialStep: { type: backendToFormTypeMapping[event?.type], id: event.id },
      stepOne: {
        eventDetails: {
          [EventFormType.FUNDRAISING_ROUND]:
            event?.type === EventTypeBackend.FUNDRAISING_ROUND
              ? {
                  enabled: true,
                  date: event.date,
                  // `closeDate` is its own column now. The fallback covers rounds closed before
                  // that column existed, where the closing date was written over `date`.
                  closeDate: event.closeDate ?? (!event.isOpen ? event.date : undefined),
                  isOpen: defaultData?.isOpen ?? event.isOpen,
                  roundingSetting: event.roundingSetting ?? InvestmentRoundingSetting.DEFAULT,
                  event: {
                    id: event.valuation.id,
                    name: event.valuation.name,
                    sharePrice: event.valuation.sharePrice,
                    issuedSharesOnStart: event.valuation.issuedSharesOnStart,
                  },
                }
              : undefined,
          [EventFormType.BUYBACK]:
            event?.type === EventTypeBackend.BUYBACK
              ? {
                  enabled: true,
                  buyBackItems: event.items.map(
                    ({ date, shareClass, sharesCount, stakeholder, id, filesLinks }) => ({
                      date,
                      shareClass,
                      shares: sharesCount,
                      stakeholder,
                      id,
                      balance: sharesCount,
                      initialShares: sharesCount,
                      initialStakeholderId: stakeholder.id,
                      initialShareClassId: shareClass.id,
                      files: mapFileLinksToFormFiles(
                        resolveTransactionManualFileLinks(event, id, filesLinks),
                      ),
                    }),
                  ),
                }
              : undefined,
          [EventFormType.CONVERSION]:
            event?.type === EventTypeBackend.CONVERSION
              ? {
                  enabled: true,
                  classConversionItems: event.items.map(
                    ({ date, fromShareClass, toShareClass, sharesCount, stakeholder, id }) => ({
                      date,
                      shareClassFrom: fromShareClass,
                      shareClassTo: toShareClass,
                      shares: sharesCount,
                      stakeholder,
                      id,
                      balance: sharesCount,
                      initialShares: sharesCount,
                      initialStakeholderId: stakeholder.id,
                      initialShareClassFromId: fromShareClass.id,
                      initialShareClassToId: toShareClass.id,
                    }),
                  ),
                }
              : undefined,
          [EventFormType.GRANT]:
            event?.type === EventTypeBackend.GRANT
              ? {
                  enabled: true,
                  ...(grantEvent?.items[0]?.plan && {
                    sharePlan: {
                      id: grantEvent.items[0].plan.id,
                      name: grantEvent.items[0].plan.name ?? '',
                      pool: { id: grantEvent.items[0].plan.pool?.id ?? '' },
                    },
                  }),
                  ...grantSignatureFormFields,
                  grantItems: event.items.map(
                    ({ date, numbersOfGrants, plan, stakeholder, id, filesLinks }) => ({
                      stakeholder,
                      grantDate: date,
                      sharePlan: plan,
                      shares: numbersOfGrants,
                      initialShares: numbersOfGrants,
                      initialSharePlanId: plan.id,
                      id,
                      balance: numbersOfGrants,
                      files: mapFileLinksToFormFiles(
                        resolveTransactionManualFileLinks(event, id, filesLinks),
                      ),
                    }),
                  ),
                }
              : undefined,
          [EventFormType.SECONDARIES]:
            event?.type === EventTypeBackend.SECONDARIES
              ? {
                  enabled: true,
                  secondariesItems: event.items.map(
                    ({
                      date,
                      fromStakeholder,
                      shareClass,
                      sharesCount,
                      sharesValue,
                      toStakeholder,
                      id,
                      filesLinks,
                    }) => ({
                      date,
                      shareClass,
                      shareValue: sharesValue,
                      stakeholderFrom: fromStakeholder,
                      stakeholderTo: toStakeholder,
                      shares: sharesCount,
                      id,
                      balance: sharesCount,
                      initialShares: sharesCount,
                      initialStakeholderFromId: fromStakeholder.id,
                      initialStakeholderToId: toStakeholder.id,
                      initialShareClassId: shareClass.id,
                      files: mapFileLinksToFormFiles(
                        resolveTransactionManualFileLinks(event, id, filesLinks),
                      ),
                    }),
                  ),
                }
              : undefined,
          [EventFormType.SHARE_ISSUANCE]:
            event?.type === EventTypeBackend.SHARE_ISSUANCE
              ? {
                  enabled: true,
                  date: event.date,
                  event: {
                    id: event.valuation.id,
                    name: event.valuation.name,
                    sharePrice: event.valuation.sharePrice,
                    issuedSharesOnStart: event.valuation.issuedSharesOnStart,
                  },
                }
              : undefined,

          [EventFormType.VALUATION]:
            event?.type === EventTypeBackend.VALUATION
              ? {
                  enabled: true,
                  date: event.date,
                  name: event.name,
                  sharePrice: event.sharePrice,
                  issuedSharesOnStart: event.issuedShares || event.issuedSharesOnStart,
                  valuationSelect: event.method,
                  // Seed the entered Pre-money value: it is no longer derived from share price in Pre-money mode.
                  preMoneyValuation:
                    event.sharePrice * (event.issuedShares || event.issuedSharesOnStart || 0),
                }
              : undefined,
        },
      },
      optionalStep: {
        [EventFormType.FUNDRAISING_ROUND]:
          event?.type === EventTypeBackend.FUNDRAISING_ROUND
            ? {
                investmentItems: event.items.map(
                  ({ date, investedValue, shareClass, stakeholder, id, filesLinks }) => ({
                    investmentDate: new Date(date),
                    stakeholder,
                    investment: investedValue,
                    shareClass,
                    id,
                    files: mapFileLinksToFormFiles(
                      resolveTransactionManualFileLinks(event, id, filesLinks),
                    ),
                  }),
                ),
              }
            : undefined,
        [EventFormType.SHARE_ISSUANCE]:
          event?.type === EventTypeBackend.SHARE_ISSUANCE
            ? {
                issuanceItems: event.items.map(
                  ({ sharesCount, shareClass, stakeholder, id, filesLinks, reverseVesting }) => ({
                    shareClass,
                    stakeholder,
                    shares: sharesCount,
                    id,
                    initialStakeholderId: stakeholder.id,
                    reverseVesting: mapReverseVestingToForm(reverseVesting),
                    files: mapFileLinksToFormFiles(
                      resolveTransactionManualFileLinks(event, id, filesLinks),
                    ),
                  }),
                ),
              }
            : undefined,
      },
      stepTwo: {
        additionalNotes: event?.additionalNotes || '',
        files: files,
      },
      companyIncorporation:
        event?.type === EventTypeBackend.COMPANY_INCORPORATION
          ? mapCompanyIncorporationEventToForm(
              event as CompanyIncorporationEvent,
              mapFileLinksToFormFiles,
            )
          : undefined,
    });

    if (event.type === EventTypeBackend.FUNDRAISING_ROUND) {
      originalInvestmentRoundingSettingRef.current =
        event.roundingSetting ?? InvestmentRoundingSetting.DEFAULT;
    }
  }, [defaultData?.isOpen, event, eventId, reset]);

  useEffect(() => {
    if (!isOpenModal || !event) return;
    if (event.type === EventTypeBackend.COMPANY_INCORPORATION) return;

    const serverLinks = isExerciseBackedEventType(event.type)
      ? resolveExerciseFamilyManualFileLinks(event)
      : resolveEventGeneralManualFileLinks(event);
    const serverSet = new Set(serverLinks);
    const docusignLinks = resolveEventAllDocusignFileLinks(event);
    const current = getValues('stepTwo.files') ?? [];

    const buildEntry = (link: string) => ({
      abort: () => '',
      loadProgress: 100 as const,
      doc: {
        name: getS3FileOriginalName(link) || '',
        size: 10,
        type: 'application/pdf',
      },
      docLink: link,
      id: uuidv4(),
    });

    const serverFiles = serverLinks.map((link) => {
      const existing = current.find((f) => f.docLink === link);
      return existing ?? buildEntry(link);
    });

    const localOnlyUploaded = current.filter(
      (f) =>
        f.docLink && !serverSet.has(f.docLink) && !isDocusignFileLink(f.docLink, docusignLinks),
    );
    const inFlight = current.filter((f) => !f.docLink);

    setValue('stepTwo.files', [...serverFiles, ...localOnlyUploaded, ...inFlight], {
      shouldValidate: true,
      shouldDirty: false,
    });

    if (!getFieldState('stepTwo.additionalNotes').isDirty) {
      setValue('stepTwo.additionalNotes', event.additionalNotes ?? '', { shouldDirty: false });
    }
  }, [event, getFieldState, getValues, isOpenModal, setValue]);

  const handleCloseModal = useCallback(() => {
    handleClose();
    invalidateQuery();
  }, [handleClose, invalidateQuery]);

  useEffect(() => {
    if (isOpenModal) {
      wasEditModalOpenRef.current = true;
      return;
    }

    if (!wasEditModalOpenRef.current) return;

    const timeoutId = setTimeout(() => {
      reset();
      wasEditModalOpenRef.current = false;
    }, SHEET_CLOSE_ANIMATION_MS);

    return () => clearTimeout(timeoutId);
  }, [isOpenModal, reset]);

  const handleUpdateData = useCallback(
    async (data: FormSchema) => {
      if (!event) return;

      const { initialStep, stepOne, optionalStep, stepTwo, companyIncorporation } = data;

      const incorporationManualFileLinks = resolveCompanyIncorporationManualFileLinks(
        companyIncorporation?.files,
      );
      const stepTwoManualFileLinks = Array.from(
        new Set(
          (stepTwo?.files ?? [])
            .map(({ docLink }) => docLink)
            .filter((docLink): docLink is string => Boolean(docLink)),
        ),
      );
      const resolvedManualFileLinks =
        initialStep.type === EventFormType.COMPANY_INCORPORATION
          ? incorporationManualFileLinks
          : stepTwoManualFileLinks;
      const resolvedFilesLinks = isExerciseBackedEventType(event.type)
        ? mergeExerciseFamilyFileLinksForSave(event, resolvedManualFileLinks)
        : mergeEventGeneralFileLinksForSave(event, resolvedManualFileLinks);
      const resolvedAdditionalNotes =
        initialStep.type === EventFormType.COMPANY_INCORPORATION
          ? (companyIncorporation?.additionalNotes ?? '')
          : (stepTwo?.additionalNotes ?? '');

      if (
        event.type === EventTypeBackend.EXERCISE ||
        event.type === EventTypeBackend.APPROVAL ||
        event.type === EventTypeBackend.PAYOUT
      ) {
        patchExerciseEventDocuments(
          {
            additionalNotes: stepTwo?.additionalNotes ?? '',
            companyId,
            eventId,
            filesLinks: resolvedFilesLinks,
          },
          {
            onSuccess: (updatedEventResponse: unknown) => {
              toast.success(messagesLocale['EVENT_SUCCESSFULLY_UPDATED']);
              handleCloseModal();
              queryClient.removeQueries({
                queryKey: [QueryKey.GET_EVENT, companyId, eventId],
                exact: true,
              });
              invalidateQuery();
              const updatedEventId =
                typeof updatedEventResponse === 'object' &&
                updatedEventResponse !== null &&
                'id' in updatedEventResponse &&
                typeof updatedEventResponse.id === 'string'
                  ? updatedEventResponse.id
                  : null;
              if (updatedEventId) {
                queryClient.invalidateQueries({
                  queryKey: [QueryKey.GET_EVENT, companyId, updatedEventId],
                  exact: true,
                });
              }
              queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS] });
              queryClient.invalidateQueries({
                queryKey: [QueryKey.GET_EVENT_TRANSACTIONS_COUNT],
              });
              invalidateVaultQueries();
            },
          },
        );
        return;
      }

      const grantSharePlan = stepOne?.eventDetails?.grant?.sharePlan;

      function mapFormTypeToBackendType(formType: EventFormType): EventTypeBackend {
        return eventTypeMapping[formType];
      }

      const type = mapFormTypeToBackendType(initialStep.type);
      const sanitizedSignatoryAssignments = buildSanitizedGrantSignatoryAssignments(
        stepOne?.eventDetails?.grant?.signatoryAssignments,
      );

      const grant = initialStep.type === EventFormType.GRANT &&
        stepOne?.eventDetails?.grant?.grantItems &&
        stepOne.eventDetails.grant.grantItems.length > 0 && {
          items: stepOne.eventDetails.grant.grantItems.map(
            ({ grantDate, sharePlan, stakeholder, shares, files, id }) => ({
              date: toBackendDateFormat(grantDate),
              planId: grantSharePlan?.id || sharePlan?.id || '',
              stakeholderId: stakeholder.id,
              numbersOfGrants: shares,
              filesLinks: mergeTransactionFileLinksForSave(
                event,
                id,
                files?.reduce<string[]>(
                  (prev, curr) => (curr.docLink ? [...prev, curr.docLink] : prev),
                  [],
                ) ?? [],
              ),
              ...(id ? { id } : {}),
            }),
          ),
          ...(stepOne.eventDetails.grant.signatureOption === GrantSignatureOption.SIGNATURE &&
            stepOne.eventDetails.grant.selectedTemplateId && {
              selectedTemplateId: stepOne.eventDetails.grant.selectedTemplateId,
              signatoryAssignments: sanitizedSignatoryAssignments,
            }),
        };

      const fundraisingRound = initialStep.type === EventFormType.FUNDRAISING_ROUND &&
        stepOne.eventDetails?.['fundraising-round']?.enabled &&
        optionalStep?.['fundraising-round']?.investmentItems && {
          valuationId: stepOne.eventDetails['fundraising-round'].event.id,
          sharePrice: stepOne.eventDetails['fundraising-round'].event.sharePrice,
          roundingSetting: stepOne.eventDetails['fundraising-round'].roundingSetting,
          isOpen: stepOne.eventDetails['fundraising-round'].isOpen,
          // `date` is always the opening date — collapsing the closing date into it on close used
          // to destroy the opening date, so a reopened round showed the date it closed on.
          date: toBackendDateFormat(stepOne.eventDetails['fundraising-round']?.date || new Date()),
          closeDate: stepOne.eventDetails['fundraising-round']?.isOpen
            ? null
            : toBackendDateFormat(
                stepOne.eventDetails['fundraising-round']?.closeDate || new Date(),
              ),
          items: optionalStep?.['fundraising-round'].investmentItems.map(
            ({ investment, investmentDate, shareClass, stakeholder, files, id }) => ({
              stakeholderId: stakeholder.id,
              shareClassId: shareClass.id,
              investedValue: investment,
              date: toBackendDateFormat(investmentDate),
              filesLinks: mergeTransactionFileLinksForSave(
                event,
                id,
                files?.reduce<string[]>(
                  (prev, curr) => (curr.docLink ? [...prev, curr.docLink] : prev),
                  [],
                ) ?? [],
              ),
              ...(id ? { id } : {}),
            }),
          ),
        };

      const issuance = initialStep.type === EventFormType.SHARE_ISSUANCE &&
        stepOne.eventDetails?.['share-issuance']?.enabled &&
        optionalStep?.['share-issuance']?.issuanceItems && {
          valuationId: stepOne.eventDetails['share-issuance'].event.id,
          date: toBackendDateFormat(stepOne.eventDetails['share-issuance'].date),
          sharePrice: stepOne.eventDetails['share-issuance'].event.sharePrice,
          items: optionalStep?.['share-issuance']?.issuanceItems.map(
            ({ shares, shareClass, stakeholder, files, id, reverseVesting }) => ({
              date: stepOne?.eventDetails['share-issuance']?.date
                ? toBackendDateFormat(stepOne.eventDetails['share-issuance'].date)
                : toBackendDateFormat(new Date()),
              stakeholderId: stakeholder.id,
              shareClassId: shareClass.id,
              sharesCount: shares,
              reverseVesting: mapReverseVestingFormToDto(reverseVesting),
              filesLinks: mergeTransactionFileLinksForSave(
                event,
                id,
                files?.reduce<string[]>(
                  (prev, curr) => (curr.docLink ? [...prev, curr.docLink] : prev),
                  [],
                ) ?? [],
              ),
              ...(id ? { id } : {}),
            }),
          ),
        };

      const buyback = initialStep.type === EventFormType.BUYBACK &&
        stepOne?.eventDetails?.buyback?.buyBackItems && {
          items: stepOne?.eventDetails.buyback?.buyBackItems.map(
            ({ date, shareClass, stakeholder, shares, files, id }) => ({
              date: toBackendDateFormat(date),
              stakeholderId: stakeholder.id,
              shareClassId: shareClass.id,
              sharesCount: shares,
              filesLinks: mergeTransactionFileLinksForSave(
                event,
                id,
                files?.reduce<string[]>(
                  (prev, curr) => (curr.docLink ? [...prev, curr.docLink] : prev),
                  [],
                ) ?? [],
              ),
              ...(id ? { id } : {}),
            }),
          ),
        };

      const secondaries = initialStep.type === EventFormType.SECONDARIES &&
        stepOne?.eventDetails?.secondaries?.secondariesItems && {
          items: stepOne?.eventDetails?.secondaries?.secondariesItems.map(
            ({
              date,
              shareClass,
              shares,
              shareValue,
              stakeholderFrom,
              stakeholderTo,
              files,
              id,
            }) => ({
              date: toBackendDateFormat(date),
              fromStakeholderId: stakeholderFrom.id,
              toStakeholderId: stakeholderTo.id,
              shareClassId: shareClass.id,
              sharesCount: shares,
              sharesValue: Number(shareValue),
              filesLinks: mergeTransactionFileLinksForSave(
                event,
                id,
                files?.reduce<string[]>(
                  (prev, curr) => (curr.docLink ? [...prev, curr.docLink] : prev),
                  [],
                ) ?? [],
              ),
              ...(id ? { id } : {}),
            }),
          ),
        };

      const conversion = initialStep.type === EventFormType.CONVERSION &&
        stepOne?.eventDetails?.conversion?.classConversionItems && {
          items: stepOne?.eventDetails?.conversion?.classConversionItems.map(
            ({ date, shares, shareClassFrom, shareClassTo, stakeholder }) => ({
              date: toBackendDateFormat(date),
              stakeholderId: stakeholder.id,
              fromShareClassId: shareClassFrom.id,
              toShareClassId: shareClassTo.id,
              sharesCount: shares,
            }),
          ),
        };

      const valuation = initialStep.type === EventFormType.VALUATION &&
        stepOne.eventDetails.valuation?.enabled && {
          name: stepOne?.eventDetails.valuation?.name,
          date: toBackendDateFormat(stepOne?.eventDetails?.valuation?.date),
          sharePrice: stepOne?.eventDetails.valuation?.sharePrice,
          method: stepOne?.eventDetails.valuation?.valuationSelect,
        };

      const companyIncorporationUpdate =
        initialStep.type === EventFormType.COMPANY_INCORPORATION && companyIncorporation
          ? mapCompanyIncorporationFormToUpdateDto(companyIncorporation)
          : undefined;

      const submitUpdate = () => {
        // Close the sheet immediately (optimistic), mirroring event creation: the row already
        // wears the pending-edit wash from useUpdate.onMutate and settles in the background;
        // onError rolls the optimistic status back.
        handleCloseModal();
        update(
          {
            companyId,
            eventId,
            // The toast waits for the queued apply to settle (list poll observes it), so it
            // agrees with the row's "Editing…" state instead of firing mid-pending.
            onApplySettled: (outcome) => {
              if (outcome === PendingMutationSettleOutcome.APPLIED) {
                toast.success(messagesLocale['EVENT_SUCCESSFULLY_UPDATED']);
              }
              if (outcome === PendingMutationSettleOutcome.FAILED) {
                toast.error(messagesLocale['EVENT_UPDATE_FAILED']);
              }
            },
            data: {
              type: type,
              grant: grant || undefined,
              fundraisingRound: fundraisingRound || undefined,
              issuance: issuance || undefined,
              buyback: buyback || undefined,
              secondaries: secondaries || undefined,
              conversion: conversion || undefined,
              valuation: valuation || undefined,
              companyIncorporation: companyIncorporationUpdate || undefined,
              filesLinks: resolvedFilesLinks,
              additionalNotes: resolvedAdditionalNotes,
            },
          },
          {
            // Deliberately does NOT touch GET_EVENT. The PATCH only enqueues the edit, and the
            // sheet stays mounted for its close animation — removing the detail key here made
            // that still-live observer refetch immediately and re-cache the PRE-apply event,
            // which is what the next open then rendered. useEvents resets GET_EVENT when the
            // queued apply actually settles.
            onSuccess: () => {
              clearRevokedAccessModalState();
              invalidateQuery();
              queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS] });
              queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS_COUNT] });
              invalidateVaultQueries();
            },
            onError: clearRevokedAccessModalState,
          },
        );
      };

      if (grant && 'selectedTemplateId' in grant) {
        const signerIds = Array.from(
          new Set([
            ...(stepOne?.eventDetails?.grant?.grantItems?.map(
              ({ stakeholder }) => stakeholder.id,
            ) ?? []),
            ...Object.values(sanitizedSignatoryAssignments).map(({ id }) => id),
          ]),
        );
        await runRevokedAccessCheck(signerIds, submitUpdate);
        return;
      }

      submitUpdate();
    },
    [
      companyId,
      event,
      eventId,
      handleCloseModal,
      invalidateQuery,
      invalidateVaultQueries,
      patchExerciseEventDocuments,
      queryClient,
      runRevokedAccessCheck,
      clearRevokedAccessModalState,
      update,
      messagesLocale,
    ],
  );

  // Every step submits the same schema, so a failure can belong to a step that is not on screen —
  // the toast then names highlighted fields the user cannot see. Move to the step that owns the
  // first failing field before toasting. The grant and company-incorporation wizards keep their
  // own step order and validation, so they stay on the plain toast.
  const handleInvalidSubmit = useCallback(
    (errors: FieldErrors<FormSchema>) => {
      const usesBaseEditSteps =
        !!event &&
        event.type !== EventTypeBackend.GRANT &&
        event.type !== EventTypeBackend.COMPANY_INCORPORATION;
      const invalidStep = usesBaseEditSteps ? resolveInvalidBaseEditStep(errors) : null;

      if (invalidStep && invalidStep !== step) {
        onEditClick({ id: eventId, step: invalidStep });
      }

      handleFormInvalidField(errors, describeInvalidFormField(errors));
    },
    [event, eventId, onEditClick, step],
  );

  const {
    toggler: confirmationModalToggler,
    isOpen: isOpenConfirmationModal,
    handleOpenModal: handleOpenConfirmationModal,
    handleCloseModal: handleCloseConfirmationModal,
    handleSuccessModal: handleSuccessConfirmationModal,
  } = useModalState({
    onSuccess: () => {
      handleSubmit(handleUpdateData, handleInvalidSubmit)();
    },
  });

  if (!isOpenModal && !event) return null;

  const isFormHydrating =
    !!event &&
    (hydratedInitialStep?.type !== backendToFormTypeMapping[event.type] ||
      hydratedInitialStep?.id !== event.id);
  const showEditModalBodyLoading = isFormHydrating;

  if (!event) {
    // Mirror the loaded branch's root structure (Fragment → SignatureRestoreAccessConfirmModal
    // + Sheet at the same child positions). React reconciles the Sheet/SheetContent as the same
    // element across the loading→loaded transition, so only the skeleton is swapped for the form
    // inside the already-open sheet — the modal must not remount and replay its open animation.
    return (
      <>
        <SignatureRestoreAccessConfirmModal
          isOpen={revokedAccessSigners.length > 0}
          onClose={clearRevokedAccessModalState}
          onConfirm={handleConfirmRestore}
          signers={revokedAccessSigners}
        />

        <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
          <SheetContent
            className="w-full max-w-[500px] border-transparent bg-transparent p-2 shadow-none"
            onInteractOutside={handleCloseModal}
            side="RIGHT"
          >
            <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
              <div className="flex h-fit w-full animate-pulse items-center justify-between bg-white px-6 py-3">
                <EditEventModalHeaderPlaceholder />
                <CloseModalButton onClose={handleCloseModal} />
              </div>
              <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
                <EditEventModalBodyPlaceholder />
                <EditEventModalFooterPlaceholder />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  const isGrantEditWizard = event.type === EventTypeBackend.GRANT && !previewMode;
  const grantUsesRecipientsEditStep = grantEditUsesSignatureRecipientsStep(
    event,
    grantSliceForEditSteps,
  );
  const grantEditPreviewStep = (grantUsesRecipientsEditStep ? 6 : 4) as EditEventStepVariant;
  const grantEditDocumentsStep = (grantUsesRecipientsEditStep ? 5 : 3) as EditEventStepVariant;

  const editFormSteps =
    event.type === EventTypeBackend.GRANT
      ? grantUsesRecipientsEditStep
        ? grantEditFormStepsWithRecipients
        : grantEditFormStepsWithoutRecipients
      : event.type === EventTypeBackend.COMPANY_INCORPORATION
        ? companyIncorporationEditFormSteps
        : baseEditFormSteps;

  const resolvedEditStep = Math.min(
    Math.max(1, step),
    editFormSteps.length,
  ) as EditEventStepVariant;

  const showExerciseDocumentsStepBack =
    (event.type === EventTypeBackend.EXERCISE ||
      event.type === EventTypeBackend.APPROVAL ||
      event.type === EventTypeBackend.PAYOUT) &&
    resolvedEditStep === 3;

  const handleNextFormStep = () => {
    if (isGrantEditWizard) {
      if (resolvedEditStep < grantEditDocumentsStep) {
        onEditClick({
          id: eventId,
          step: (resolvedEditStep + 1) as EditEventStepVariant,
        });
        return;
      }
      if (resolvedEditStep === grantEditDocumentsStep) {
        handleOpenConfirmationModal();
      }
      return;
    }
    handleOpenConfirmationModal();
  };

  const eventName =
    event.type === EventTypeBackend.VALUATION
      ? event.name
      : event.type === EventTypeBackend.COMPANY_INCORPORATION
        ? getTranslation('COMPANY_INCORPORATION')
        : event.type === EventTypeBackend.FUNDRAISING_ROUND ||
            event.type === EventTypeBackend.SHARE_ISSUANCE
          ? event.valuation.name
          : getTranslation(
              (initialStep?.type ? EventTypeTitleString[initialStep.type] : undefined) ??
                eventFormType[event.type],
            );

  const fundraisingConfirmVariant = (() => {
    if (event.type !== EventTypeBackend.FUNDRAISING_ROUND) return undefined;

    const original = originalInvestmentRoundingSettingRef.current;
    const current = getValues('stepOne.eventDetails.fundraising-round')?.roundingSetting;

    if (!original || !current || original === current) return undefined;

    return 'rounding_recalc' as const;
  })();

  const grantConfirmVariant = (() => {
    if (event.type !== EventTypeBackend.GRANT) return undefined;
    const ge = event as GrantEvent;
    const g = getValues('stepOne.eventDetails.grant');
    const hadTemplateSignatureFlow = grantUsesTemplateSignatureWizard(ge);
    const sig = g?.signatureOption;

    if (!hadTemplateSignatureFlow && sig !== GrantSignatureOption.SIGNATURE) return undefined;
    if (!hadTemplateSignatureFlow && sig === GrantSignatureOption.SIGNATURE)
      return 'signature_update' as const;
    return 'signature_cancel' as const;
  })();

  const grantEditSubtitle = (() => {
    if (resolvedEditStep !== grantEditPreviewStep || initialStep?.type !== EventFormType.GRANT)
      return null;
    const g = getValues('stepOne.eventDetails.grant');
    const planName = g?.sharePlan?.name;
    if (!planName) return null;
    return {
      planName,
      withSignature:
        g?.signatureOption === GrantSignatureOption.SIGNATURE && !!g?.selectedTemplateId,
    };
  })();

  const modalEventTypeLabel = EventTypeButtonTitle[initialStep?.type] ?? (
    <AppFormattedMessage id={eventFormType[event.type]} />
  );

  const exerciseLikeModalTitle = (() => {
    if (event.type !== EventTypeBackend.EXERCISE && event.type !== EventTypeBackend.APPROVAL)
      return null;
    if (!('exercise' in event) || !event.exercise?.items?.length) return null;
    const planKind = event.exercise.items[0].vestingTask.grantItem.plan.type;
    const planTypeLabel = <AppFormattedMessage id={sharePlanTypeTitle[planKind]} />;
    return previewMode ? (
      <AppFormattedMessage id="VIEW_EXERCISE_EVENT" values={{ planType: planTypeLabel }} />
    ) : (
      <AppFormattedMessage id="EDIT_EXERCISE_EVENT" values={{ planType: planTypeLabel }} />
    );
  })();

  return (
    <>
      <SignatureRestoreAccessConfirmModal
        isOpen={revokedAccessSigners.length > 0}
        onClose={clearRevokedAccessModalState}
        onConfirm={handleConfirmRestore}
        signers={revokedAccessSigners}
      />

      <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
        <SheetContent
          className="w-full max-w-[500px] border-transparent bg-transparent p-2 shadow-none"
          onInteractOutside={handleCloseModal}
          side="RIGHT"
        >
          <AlertDialogWrapper
            control={{ onOpenChange: confirmationModalToggler, open: isOpenConfirmationModal }}
          >
            <ConfirmUpdateEventModal
              companyIncorporationConfirm={event.type === EventTypeBackend.COMPANY_INCORPORATION}
              eventName={eventName}
              fundraisingConfirmVariant={fundraisingConfirmVariant}
              grantConfirmVariant={grantConfirmVariant}
              onClose={handleCloseConfirmationModal}
              onSuccess={handleSuccessConfirmationModal}
            />
          </AlertDialogWrapper>
          <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
            <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
              <div className="flex min-w-0 flex-col pr-2">
                <span className="truncate text-xl font-[550] text-gray-700">
                  {exerciseLikeModalTitle ??
                    (previewMode ? (
                      <AppFormattedMessage
                        id="VIEW_POOL"
                        values={{
                          text: modalEventTypeLabel,
                        }}
                      />
                    ) : (
                      <AppFormattedMessage
                        id="EDIT_POOL"
                        values={{
                          text: modalEventTypeLabel,
                        }}
                      />
                    ))}
                </span>
                {grantEditSubtitle && (
                  <span className="block text-xs font-[450] text-gray-500">
                    <AppFormattedMessage
                      id={
                        grantEditSubtitle.withSignature
                          ? 'GRANT_EVENT_MODAL_SUBTITLE_SIGNATURE'
                          : 'GRANT_EVENT_MODAL_SUBTITLE_NO_SIGNATURE'
                      }
                      values={{ planName: grantEditSubtitle.planName }}
                    />
                  </span>
                )}
              </div>
              <CloseModalButton onClose={handleCloseModal} />
            </div>
            <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
              {showEditModalBodyLoading ? (
                <>
                  <EditEventModalBodyPlaceholder />
                  <EditEventModalFooterPlaceholder />
                </>
              ) : (
                <FadeIn className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
                  {editFormSteps[resolvedEditStep - 1]({
                    companyId,
                    issuedShares,
                    setError,
                    formData: getValues,
                    nextFormStep: handleNextFormStep,
                    prevFormStep: () => {
                      if (isGrantEditWizard && resolvedEditStep === 1) {
                        onEditClick({ id: eventId, step: grantEditPreviewStep });
                        return;
                      }
                      if (showExerciseDocumentsStepBack) {
                        onEditClick({
                          id: eventId,
                          step: editFormSteps.length as EditEventStepVariant,
                        });
                        return;
                      }
                      if (resolvedEditStep > 1) {
                        onEditClick({
                          id: eventId,
                          step: (resolvedEditStep - 1) as EditEventStepVariant,
                        });
                      }
                    },
                    setFormData: setValue,
                    control,
                    handleCloseModal,
                    filedState: getFieldState,
                    errors,
                    clearErrors,
                    lockMode: true,
                    previewMode,
                    onEditClick: (step) => onEditClick({ id: eventId, step }),
                    transactionId,
                    reset,
                    isLoading:
                      isPending || isCheckingRevokedAccess || revokedAccessSigners.length > 0,
                    event,
                    isGrantEditWizard,
                    editWizardStep: resolvedEditStep,
                    showDocumentsStepBack: showExerciseDocumentsStepBack,
                  })}
                </FadeIn>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
